/** Controles de zoom do PDF (Fase 10 US-06).
 *
 * Botões com passo, e não um slider: o slider emite um evento por pixel
 * arrastado, e cada evento é um re-render de página inteira no pdf.js — mesmo
 * com *debounce* o gesto fica arrastando. Botão é um passo por clique, tem alvo
 * de toque de 44px e é operável por teclado sem truque. O ajuste contínuo já
 * existe no painel de acessibilidade, que é onde o aluno espera achá-lo.
 *
 * O percentual mostrado é o do zoom EFETIVO, não o pedido: quando o orçamento
 * de pixels obriga a reduzir (iOS), o número tem de contar a verdade, senão o
 * aluno clica em "+" e vê 300% com a mesma página de antes.
 */
import { ESCALA_MAX } from '@/features/reader/utils/scale'
import { ESCALA_MIN, comPasso } from '@/features/reader/utils/zoom'

interface ZoomControlsProps {
  /** Escala efetivamente desenhada. */
  escala: number
  /** `true` quando o aluno fixou um zoom; `false` = ajustado à largura. */
  manual: boolean
  onZoom: (escala: number | null) => void
}

export function ZoomControls({ escala, manual, onZoom }: ZoomControlsProps) {
  const pct = Math.round(escala * 100)

  return (
    <div className="pdf-zoom" role="group" aria-label="Zoom da página">
      <button
        type="button"
        onClick={() => onZoom(comPasso(escala, -1))}
        disabled={escala <= ESCALA_MIN}
        aria-label="Reduzir o zoom"
      >
        −
      </button>

      {/* `aria-live` para quem não vê o botão mudar de estado ouvir o efeito. */}
      <span className="pdf-zoom__valor" aria-live="polite">
        {pct}%
      </span>

      <button
        type="button"
        onClick={() => onZoom(comPasso(escala, 1))}
        disabled={escala >= ESCALA_MAX}
        aria-label="Aumentar o zoom"
      >
        +
      </button>

      <button
        type="button"
        onClick={() => onZoom(null)}
        disabled={!manual}
        aria-label="Ajustar a página à largura da tela"
        aria-pressed={!manual}
      >
        {manual ? 'Ajustar' : 'Automático'}
      </button>
    </div>
  )
}
